import React, { useEffect, useState } from "react";
import { authApi } from "../api";
import { Recycle, X } from "lucide-react";

const ScrapPool = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ balance_grams: 0, contributions: [] });
  const [materials, setMaterials] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [form, setForm] = useState({
    material_id: "",
    weight_grams: "",
    purity: "",
    notes: "",
  });

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [poolRes, matsRes] = await Promise.all([
        authApi.get("/inventory/scrap-pool"),
        authApi.get("/inventory/materials", { params: { per_page: 500 } }),
      ]);
      setData({
        balance_grams: Number(poolRes.data?.balance_grams || poolRes.data?.scrap_pool_grams || 0),
        contributions: poolRes.data?.contributions || [],
      });
      setMaterials(matsRes.data?.materials || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load scrap pool data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const submitConvert = async () => {
    setConverting(true);
    setError("");
    setSuccessMsg("");
    try {
      const payload = {
        material_id: Number(form.material_id),
        weight_grams: Number(form.weight_grams),
        purity: form.purity,
        notes: form.notes,
      };
      const res = await authApi.post("/inventory/scrap-pool/convert", payload);
      setSuccessMsg(
        `New batch ${res.data?.new_batch_code} created with ${Number(res.data?.weight_converted || 0).toFixed(3)}g`
      );
      setModalOpen(false);
      setForm({ material_id: "", weight_grams: "", purity: "", notes: "" });
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to convert scrap.");
    } finally {
      setConverting(false);
    }
  };

  let cumulative = 0;

  return (
    <div className="h-full overflow-y-auto bg-slate-50 p-4 md:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Recycle size={20} className="text-blue-600" /> Scrap Pool
          </h1>
          <p className="text-sm text-slate-500">Track scrap contributions and convert to fresh production batches.</p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          Convert to New Batch
        </button>
      </div>

      {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}
      {successMsg && <div className="mt-4 rounded-lg border border-green-200 bg-green-50 text-green-700 px-4 py-3 text-sm">{successMsg}</div>}

      <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="text-xs uppercase tracking-wide text-slate-500">Current Balance</div>
        <div className="text-3xl font-bold text-slate-800 mt-2">{Number(data.balance_grams || 0).toFixed(3)} g</div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Job Card</th>
              <th className="px-4 py-3 text-left">Design</th>
              <th className="px-4 py-3 text-left">Stage</th>
              <th className="px-4 py-3 text-right">Grams Added</th>
              <th className="px-4 py-3 text-right">Cumulative</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && (data.contributions || []).map((r, idx) => {
              cumulative += Number(r.grams || 0);
              return (
                <tr key={`${r.date || "x"}-${idx}`}>
                  <td className="px-4 py-3">{r.date ? new Date(r.date).toLocaleString() : "N/A"}</td>
                  <td className="px-4 py-3">{r.job_card_no || r.job_card_id || "N/A"}</td>
                  <td className="px-4 py-3">{r.design_name || "N/A"}</td>
                  <td className="px-4 py-3">{r.stage_code || "N/A"}</td>
                  <td className="px-4 py-3 text-right font-mono">{Number(r.grams || 0).toFixed(3)}</td>
                  <td className="px-4 py-3 text-right font-mono">{Number(r.cumulative_balance ?? cumulative).toFixed(3)}</td>
                </tr>
              );
            })}
            {!loading && (!data.contributions || data.contributions.length === 0) && (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-slate-400">No scrap contributions found.</td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-slate-400">Loading scrap history...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Convert Scrap to New Batch</h3>
              <button onClick={() => setModalOpen(false)} className="text-slate-500 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-500">Target Material</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={form.material_id}
                  onChange={(e) => setForm({ ...form, material_id: e.target.value })}
                >
                  <option value="">Select material</option>
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Weight (grams)</label>
                <input
                  type="number"
                  step="0.001"
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={form.weight_grams}
                  onChange={(e) => setForm({ ...form, weight_grams: e.target.value })}
                  placeholder={`Max ${Number(data.balance_grams || 0).toFixed(3)} g`}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Purity</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={form.purity}
                  onChange={(e) => setForm({ ...form, purity: e.target.value })}
                  placeholder="e.g. 92.5%"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Notes</label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 border rounded-lg text-slate-600">Cancel</button>
              <button
                onClick={submitConvert}
                disabled={converting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
              >
                {converting ? "Converting..." : "Convert"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapPool;
